import clsx from 'clsx';
import { motion } from 'framer-motion';
import { Database, Brain, Scale, ShieldCheck, Zap, Radio } from 'lucide-react';

// ── Types ─────────────────────────────────────────────────────────────────────

interface OrchestrationFlowVisualizerProps {
  activeAgentCount: number;
  totalRecs: number;
  strongBets: number;
  avgConfidence: number | null;
  isConnected: boolean;
  gameCount?: number;
  className?: string;
}

// ── Flow connector ────────────────────────────────────────────────────────────

function FlowConnector({ active }: { active: boolean }) {
  return (
    <div className="flex items-center flex-shrink-0 px-1">
      <motion.div
        className="flex items-center gap-px"
        animate={active ? { opacity: [0.4, 1, 0.4] } : { opacity: 0.3 }}
        transition={active ? { duration: 1.8, repeat: Infinity, ease: 'linear' } : {}}
      >
        <span className={clsx('block w-2 h-px', active ? 'bg-purple/70' : 'bg-surface-4')} />
        <span className={clsx('block w-2 h-px', active ? 'bg-purple/70' : 'bg-surface-4')} />
        <span
          className="block w-0 h-0 border-t-[3px] border-b-[3px] border-l-[5px] border-t-transparent border-b-transparent"
          style={{ borderLeftColor: active ? 'rgba(139,92,246,0.7)' : 'rgba(31,45,61,1)' }}
        />
      </motion.div>
    </div>
  );
}

// ── Flow node ─────────────────────────────────────────────────────────────────

interface FlowNodeProps {
  icon: React.FC<{ className?: string }>;
  label: string;
  sub: string;
  metric?: string;
  metricColor?: string;
  active: boolean;
}

function FlowNode({ icon: Icon, label, sub, metric, metricColor, active }: FlowNodeProps) {
  return (
    <motion.div
      layout
      className={clsx(
        'flex flex-col gap-2 p-3 rounded-xl border min-w-[108px] transition-all duration-300 flex-shrink-0',
        active ? 'border-purple/30 bg-purple/8' : 'border-surface-4 bg-surface-1',
      )}
    >
      <div className="flex items-center gap-2">
        <div className={clsx(
          'w-6 h-6 rounded-lg flex items-center justify-center border flex-shrink-0',
          active ? 'bg-purple/15 border-purple/30' : 'bg-surface-2 border-surface-4',
        )}>
          <Icon className={clsx('w-3 h-3', active ? 'text-purple-bright' : 'text-gray-600')} />
        </div>
        {active ? (
          <span className="relative flex h-1.5 w-1.5 flex-shrink-0">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-purple opacity-60" />
            <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-purple" />
          </span>
        ) : (
          <span className="h-1.5 w-1.5 rounded-full bg-surface-4 flex-shrink-0" />
        )}
      </div>

      <div>
        <p className={clsx('text-[10px] font-bold leading-tight', active ? 'text-purple-bright' : 'text-gray-400')}>
          {label}
        </p>
        <p className="text-[9px] text-gray-600 leading-tight mt-0.5">{sub}</p>
      </div>

      {metric !== undefined && (
        <p className={clsx(
          'text-[10px] font-mono font-bold tabular-nums',
          metricColor ?? (active ? 'text-purple/80' : 'text-gray-600'),
        )}>
          {metric}
        </p>
      )}
    </motion.div>
  );
}

// ── OrchestrationFlowVisualizer ───────────────────────────────────────────────

export function OrchestrationFlowVisualizer({
  activeAgentCount,
  totalRecs,
  strongBets,
  avgConfidence,
  isConnected,
  gameCount = 0,
  className,
}: OrchestrationFlowVisualizerProps) {
  const hasRecs = totalRecs > 0;
  const hasAgents = activeAgentCount > 0;

  const nodes = [
    {
      icon: Database,
      label: 'Game Context',
      sub: 'Stats · Odds · Weather',
      metric: gameCount > 0 ? `${gameCount} games` : '—',
      metricColor: gameCount > 0 ? 'text-brand' : undefined,
      active: isConnected && gameCount > 0,
    },
    {
      icon: Brain,
      label: '9 Agents',
      sub: 'Parallel analysis',
      metric: hasAgents ? `${activeAgentCount} active` : 'Idle',
      metricColor: hasAgents ? 'text-purple-bright' : undefined,
      active: hasAgents,
    },
    {
      icon: Scale,
      label: 'Weighted Score',
      sub: 'Agent consensus',
      metric: avgConfidence !== null ? `${(avgConfidence * 100).toFixed(0)}% avg` : '—',
      metricColor: avgConfidence !== null
        ? avgConfidence >= 0.7 ? 'text-win' : avgConfidence >= 0.55 ? 'text-warn' : 'text-gray-400'
        : undefined,
      active: hasRecs,
    },
    {
      icon: ShieldCheck,
      label: 'Trust Gate',
      sub: 'Data quality check',
      metric: hasRecs ? 'Pass' : '—',
      metricColor: hasRecs ? 'text-win' : undefined,
      active: hasRecs,
    },
    {
      icon: Zap,
      label: 'Decision',
      sub: 'STRONG / QUALIFIED',
      metric: strongBets > 0 ? `${strongBets} STRONG` : totalRecs > 0 ? `${totalRecs} recs` : '—',
      metricColor: strongBets > 0 ? 'text-gold' : totalRecs > 0 ? 'text-brand' : undefined,
      active: hasRecs,
    },
    {
      icon: Radio,
      label: 'SSE Publish',
      sub: 'Live broadcast',
      metric: isConnected ? 'Connected' : 'Offline',
      metricColor: isConnected ? 'text-win' : 'text-loss',
      active: isConnected,
    },
  ];

  return (
    <div className={clsx('overflow-x-auto scrollbar-hide', className)}>
      <div className="flex items-center gap-0 min-w-max">
        {nodes.map((node, i) => (
          <div key={node.label} className="flex items-center">
            <FlowNode {...node} />
            {i < nodes.length - 1 && <FlowConnector active={node.active && nodes[i + 1].active} />}
          </div>
        ))}
      </div>
    </div>
  );
}
